import React from 'react';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';

import extractDialogProps from 'components/extractDialogProps';
import useClient from 'components/Auth/useClient';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
  },
  dialogContent: {
    width: '100%'
  },
  actions: {
    width: '100%',
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  wrapper: {
    margin: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    // color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -8,
    marginLeft: -12,
  },
}));

function RecreateDialog(props)
{
  const [dialogProps] = extractDialogProps(props);
  const {
    id,
    contentId,
    handleBeginRecreate,
    handleClose,
  } = props;

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const client = useClient();
  const handleRecreate = React.useCallback(() => {
    client.post('api/document/recreate/' + id + '/' + contentId)
      .then(() => {
        enqueueSnackbar('再作成を受け付けました。', { style: { whiteSpace: 'pre-line' } });
        handleBeginRecreate();
        handleClose();
      })
      .catch(() => {
        enqueueSnackbar('再作成の受付に失敗しました。時間を置いて再度実行してください。', { variant: 'error' });
      });
  }, [client, id, contentId]);

  return (
    <Dialog
      {...dialogProps}
      aria-labelledby="recreate-dialog-title"
      aria-describedby="recreate-dialog-description"
      className={classes.dialog}
    >
      <Container className={classes.container}>
        <DialogTitle id="recreate-dialog-title">PDFの再作成</DialogTitle>
        <DialogContent dividers={true} className={classes.dialogContent}>
          <Typography>
            正常にPDFが作成されていない場合に使用して下さい。
          </Typography>
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            className={classes.submit}
            onClick={handleRecreate}>
            再作成する
          </Button>
        </DialogActions>
      </Container>
    </Dialog>);
}


export default function DownloadDialog(props) {
  const [dialogProps] = extractDialogProps(props);
  const {
    id,
    contentId,
    title,
    download,
  } = props;

  const [downloadPath, setDownloadPath] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [openRecreate, setOpenRecreate] = React.useState(false);
  const [recreating, setRecreating] = React.useState(false);
  const hadleOpenRecreate = React.useCallback(() => {
    setOpenRecreate(true);
  }, []);
  const hadleCloseRecreate = React.useCallback(() => {
    setOpenRecreate(false);
  }, []);
  const handleBeginRecreate = React.useCallback(() => {
    setRecreating(true);
  }, []);

  const client = useClient();
  const getDownloadPath = React.useCallback((retryable) => {
    client.post('api/document/status/' + id + '/' + contentId)
      .then((response) => {
        setDownloadPath(response.data.downloadPath);
        setRecreating(false);
        setLoading(false);
      })
      .catch((error) => {
        if (retryable && error.response.status === 404) {
          // ダウンロードパスが取得できない場合、再作成処理を実行
          client.post('api/document/recreate/' + id + '/' + contentId);
          setRecreating(true);
          setLoading(false);
        }
      });
  }, [id, contentId])

  React.useEffect(() => {
    if (loading)
    {
      getDownloadPath(true);
    }
    if (recreating)
    {
      const interval = setInterval(() => getDownloadPath(false), /*delay=*/3000);
      return () => clearInterval(interval);
    }
  }, [loading, recreating]);

  const classes = useStyles();
  return (
    <React.Fragment>
      <Dialog
        {...dialogProps}
        aria-labelledby="download-dialog-title"
        aria-describedby="download-dialog-description"
        className={classes.dialog}
      >
        <Container className={classes.container}>
          <DialogTitle id="download-dialog-title">ダウンロード</DialogTitle>
          <DialogContent dividers={true} className={classes.dialogContent}>
            <Typography>
              {title}のPDFファイルをダウンロードします。
            </Typography>
          </DialogContent>
          <DialogActions className={classes.actions}>
            {
              !recreating &&
              <Button
                variant="contained"
                color="primary"
                className={classes.submit}
                onClick={hadleOpenRecreate}>
                再作成
              </Button>
            }
            <div className={classes.wrapper}>
              <Button
                component="a"
                href={downloadPath}
                download={download}
                variant="contained"
                color="primary"
                disabled={recreating || !downloadPath}
                className={classes.submit}>
                ダウンロード
              </Button>
              {(recreating || loading) && <CircularProgress size={24} className={classes.buttonProgress} />}
            </div>
          </DialogActions>
        </Container>
      </Dialog>
      <RecreateDialog
        open={openRecreate}
        id={id}
        contentId={contentId}
        handleClose={hadleCloseRecreate}
        handleBeginRecreate={handleBeginRecreate}
      />
    </React.Fragment>
  );
}